import axios from 'axios';
import { forbiddenMessage } from './forbidden';

function resolveBackendUrl() {
  // Config injetada em runtime pelo entrypoint do container (lê PUBLIC_URL do
  // ambiente do `docker run`, gerando public/env-config.js antes do `serve`
  // subir). Permite trocar o backend sem rebuildar a imagem. Ver Dockerfile/entrypoint.sh.
  const runtimeUrl = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.BACKEND_URL || '').trim();
  if (runtimeUrl) return runtimeUrl;

  return (process.env.REACT_APP_BACKEND_URL || '').trim();
}

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;

// Exporta para debug/uso externo (ex: shellWsUrl)
export const RESOLVED_BACKEND_URL = BACKEND_URL;

const api = axios.create({
  baseURL: API,
  timeout: 25000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('oltm_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('oltm_token');
      localStorage.removeItem('oltm_user');
      if (window.location.pathname.startsWith('/app')) {
        window.location.href = '/login';
      }
    }
    // Sessão 6 (docs/RevisaoColeta2908/06): a leitura ao vivo na OLT concorre
    // com a coleta pelo mesmo orçamento de sessões CLI. Quando não sobra sessão
    // a tempo o backend responde 503 { error: "coleta_ocupada", retry_after_s }.
    // Troca o código pela mensagem amigável aqui, no ponto único, pra todos os
    // catch(err.response?.data?.error) da app pegarem de graça.
    if (error.response?.status === 503 && error.response?.data?.error === 'coleta_ocupada') {
      const s = error.response.data.retry_after_s || 15;
      error.response.data.error = `A OLT está ocupada com a coleta agora — tente de novo em ~${s}s.`;
    }
    // RVF-02: 403 do RBAC ("missing permission: x:y") vira texto de operador.
    if (error.response?.status === 403 && error.response.data && typeof error.response.data === 'object') {
      let lang = 'pt';
      try { lang = localStorage.getItem('oltm_lang') || 'pt'; } catch { /* sem storage */ }
      const friendly = forbiddenMessage(error.response.data.error, lang);
      if (friendly) error.response.data.error = friendly;
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
};

// 2FA (TOTP) — só opera sobre o usuário autenticado ("me").
// Fluxos:
//  · Habilitar:    POST /auth/me/2fa/enable   → { secret, otpauth_url }
//                  POST /auth/me/2fa/verify   { code } → { message, backup_codes }
//  · Desabilitar:  POST /auth/me/2fa/disable  { password, code }
//  · Login com 2FA: /auth/login retorna { two_fa_required, pending_token } →
//                   POST /auth/2fa/login-verify { pending_token, code } → { token, user }
export const twoFaAPI = {
  enable:      () => api.post('/auth/me/2fa/enable'),
  verify:      (code) => api.post('/auth/me/2fa/verify', { code }),
  disable:     (password, code) => api.post('/auth/me/2fa/disable', { password, code }),
  loginVerify: (pending_token, code) => api.post('/auth/2fa/login-verify', { pending_token, code }),
};

export const geoAPI = {
  get: () => api.get('/geo'),
};

export const statsAPI = {
  live: () => api.get('/stats'),
  fx: () => api.get('/fx'),
};

export const dashboardAPI = {
  kpis: () => api.get('/dashboard/kpis'),
  networkStatus: (hours) => api.get('/dashboard/network-status', { params: hours ? { hours } : {} }),
  authorizationsPerDay: (days) => api.get('/dashboard/authorizations-per-day', { params: days ? { days } : {} }),
  ponOutages: () => api.get('/dashboard/pon-outages'),
  oltSummary: () => api.get('/dashboard/olt-summary'),
};

export const userAPI = {
  list: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  toggleStatus: (id) => api.put(`/users/${id}/toggle-status`),
  resetPassword: (id, newPassword, mustChange = true) => api.post(`/users/${id}/reset-password`, {
    ...(newPassword ? { password: newPassword } : {}),
    must_change_password: mustChange,
  }),
  testTelegram: (id) => api.post(`/users/${id}/telegram/test`),
};

// v1 · Branding (identidade visual da instância) + White-label
export const brandingAPI = {
  get:            () => api.get('/v1/branding'),
  update:         (data) => api.put('/v1/branding', data),
  // Subconjunto público (sem JWT) usado pela tela de login para pintar logo/
  // imagem de fundo antes do usuário se autenticar.
  getPublic:      () => api.get('/branding/public'),
  // /v1/settings/branding é um subconjunto (company_name/logo_url/primary_color) do
  // que /v1/branding já cobre. Não tem tela própria — a aba Geral (BrandGeneralSection)
  // mantém os dois em sincronia no save, chamando update() e updateVisual() juntos.
  getVisual:      () => api.get('/v1/settings/branding'),
  updateVisual:   (data) => api.put('/v1/settings/branding', data),
  // /v1/settings é um recurso distinto (config operacional: timezone, poll interval,
  // webhook, SNMP trap etc.) — não é branding, mas ficou nesse objeto por conveniência.
  // Sem tela própria ainda; ver memória "endpoint test 2026-07-21" para detalhes.
  getSettings:    () => api.get('/v1/settings'),
  updateSettings: (data) => api.put('/v1/settings', data),
};

// v1 · Settings globais SMTP + Telegram (admin) — usados pelo motor de notificações real
export const smtpSettingsAPI = {
  get:    () => api.get('/v1/settings/smtp'),
  update: (data) => api.put('/v1/settings/smtp', data),   // { host, port, username, password, encryption, from_email, from_name, is_enabled }
  test:   (to) => api.post('/v1/settings/smtp/test', { to }),
};

export const telegramSettingsAPI = {
  get:    () => api.get('/v1/settings/telegram'),
  update: (data) => api.put('/v1/settings/telegram', data),   // { bot_token, is_enabled }
  test:   (chatId) => api.post('/v1/settings/telegram/test', { chat_id: chatId }),
};

// v1 · Debounce de notificação (nº de ciclos de poll consecutivos antes de
// disparar onu.down/onu.up/olt.offline/olt.online) — 1 = comportamento atual
export const alertSettingsAPI = {
  get:    () => api.get('/v1/settings/alerts'),
  update: (confirmationsRequired) => api.put('/v1/settings/alerts', { confirmations_required: confirmationsRequired }),
};


// v1 · RBAC (grupos de permissão + catálogo de permissões)
export const rbacAPI = {
  listPermissions:  () => api.get('/v1/permissions'),
  listGroups:       () => api.get('/v1/user-groups'),
  getGroup:         (id) => api.get(`/v1/user-groups/${id}`),
  createGroup:      (data) => api.post('/v1/user-groups', data),
  updateGroup:      (id, data) => api.put(`/v1/user-groups/${id}`, data),
  deleteGroup:      (id) => api.delete(`/v1/user-groups/${id}`),
  groupMembers:     (id) => api.get(`/v1/user-groups/${id}/members`),
  addPermissions:   (id, permissions) => api.post(`/v1/user-groups/${id}/permissions`, { permissions }),
  removePermissions:(id, permissions) => api.delete(`/v1/user-groups/${id}/permissions`, { data: { permissions } }),
};

export const oltAPI = {
  list: () => api.get('/olts'),
  get: (id) => api.get(`/olts/${id}`),
  create: (data) => api.post('/olts', data),
  update: (id, data) => api.put(`/olts/${id}`, data),
  delete: (id) => api.delete(`/olts/${id}`),
  // Testa a conectividade com um OLT ainda não salvo (usa as credenciais
  // digitadas no formulário de cadastro) — deliberadamente fora de
  // /olts/{id} no backend, então não exige licença. Retorna tcp/ssh/snmp +
  // identity (product/version/patch/serial lidos do device de verdade).
  testConnectionPreSave: (data) => api.post('/olts/test-connection', data),
  metrics: (id, params) => api.get(`/olts/${id}/metrics`, { params }),
  onuCentroid: (id) => api.get(`/olts/${id}/onu-centroid`),
  // Endpoint dedicado e auditado para revelar credenciais em texto puro —
  // só deve ser chamado sob clique explícito do usuário, nunca pré-carregado.
  credentials: (id) => api.get(`/olts/${id}/credentials`),
  // Dados do equipamento (foto/categoria/descrição) puxados do catálogo
  // central do SaaS Manager, resolvidos por vendor+model — 204 sem corpo
  // quando não há nada pra mostrar (SaaS Manager não configurado, ou tipo
  // ainda não sincronizado).
  equipment: (id) => api.get(`/olts/${id}/equipment`),
};

// v1 · Novos endpoints da OLT: placas, portas PON, ONUs, coleta SNMP/SSH
export const oltV1API = {
  boardsLive:     (oltId) => api.get(`/v1/olts/${oltId}/boards-live`),
  boards:         (oltId) => api.get(`/v1/olts/${oltId}/boards`),
  boardGet:       (oltId, boardId) => api.get(`/v1/olts/${oltId}/boards/${boardId}`),
  boardsDetect:   (oltId) => api.post(`/v1/olts/${oltId}/boards/detect`, {}, { timeout: 50000 }),
  boardReboot:    (oltId, slot) => api.post(`/v1/olts/${oltId}/boards/${slot}/reboot`, {}, { timeout: 35000 }),
  ponPortsLive:   (oltId) => api.get(`/v1/olts/${oltId}/pon-ports-live`),
  ponPorts:       (oltId) => api.get(`/v1/olts/${oltId}/pon-ports`),
  ponPortGet:     (oltId, portId) => api.get(`/v1/olts/${oltId}/pon-ports/${portId}`),
  ponPortMetrics: (oltId, portId, params) => api.get(`/v1/olts/${oltId}/pon-ports/${portId}/metrics`, { params }),
  ponPortOnus:    (oltId, portId) => api.get(`/v1/olts/${oltId}/pon-ports/${portId}/onus`),
  ponPortUpdate:  (oltId, portId, body) => api.put(`/v1/olts/${oltId}/pon-ports/${portId}`, body),
  ponPortLive:    (oltId, portId) => api.get(`/v1/olts/${oltId}/pon-ports/${portId}/live`, { timeout: 25000 }),
  // Leitura sob demanda de tráfego (up/down por ONU) desta porta PON, pensada pra ser
  // chamada repetidamente enquanto a tela ao vivo está aberta — ver
  // DocsColetadeTrafego/03 e PonDetailPage.jsx's live-traffic polling loop.
  ponPortTrafficLive: (oltId, portId) => api.get(`/v1/olts/${oltId}/pon-ports/${portId}/traffic-live`, { timeout: 25000 }),
  ponPortRebootOnus: (oltId, portId) => api.post(`/v1/olts/${oltId}/pon-ports/${portId}/reboot-onus`, {}, { timeout: 35000 }),
  // Eventos agregados de queda por porta PON (DocsSmartOltInspiracao/03-correlacao-queda-por-pon.md)
  ponEvents:      (oltId) => api.get(`/v1/olts/${oltId}/pon-events`),
  onusLive:       (oltId) => api.get(`/v1/olts/${oltId}/onus-live`),
  onuOpticalHistory: (oltId, serial, params) => api.get(`/v1/olts/${oltId}/onus-live/${serial}/optical-history`, { params }),
  fanHistory:     (oltId, params) => api.get(`/v1/olts/${oltId}/fan-history`, { params }),
  fansLive:       (oltId) => api.get(`/v1/olts/${oltId}/fans-live`),
  fanConfig:      (oltId) => api.get(`/v1/olts/${oltId}/fan-config`),
  updateFanConfig:(oltId, data) => api.put(`/v1/olts/${oltId}/fan-config`, data),
  networkInterfacesLive: (oltId) => api.get(`/v1/olts/${oltId}/network-interfaces-live`),
  uplinksLive:    (oltId) => api.get(`/v1/olts/${oltId}/uplinks-live`),
  uplinkUpdate:   (oltId, uplinkId, body) => api.put(`/v1/olts/${oltId}/uplinks/${uplinkId}`, body),
  // Leitura sob demanda de tráfego IN/OUT (via SNMP ifHCIn/OutOctets) desta porta uplink,
  // pensada pra ser chamada repetidamente enquanto um painel ao vivo está aberto — mesmo
  // padrão de ponPortTrafficLive, ver GraphsPage.jsx's UplinkLivePanel.
  uplinkTrafficLive: (oltId, uplinkId) => api.get(`/v1/olts/${oltId}/uplinks/${uplinkId}/traffic-live`, { timeout: 15000 }),
  vlansLive:      (oltId, params) => api.get(`/v1/olts/${oltId}/vlans-live`, params ? { params } : undefined),
  vlansDeleteBatch: (oltId, vlans) => api.post(`/v1/olts/${oltId}/vlans/delete-batch`, { vlans }),
  createVlans:    (oltId, body) => api.post(`/v1/olts/${oltId}/vlans`, body),
  deleteVlan:     (oltId, vlanId) => api.delete(`/v1/olts/${oltId}/vlans/${vlanId}`),
  // live=true lê também o estado real de LAN-to-LAN na OLT (só com VLAN_L2_FEATURES_ENABLED).
  vlanGet:        (oltId, vlanId, { live = false } = {}) => api.get(`/v1/olts/${oltId}/vlans/${vlanId}`, live ? { params: { live: 1 } } : undefined),
  vlanUpdate:     (oltId, vlanId, body) => api.put(`/v1/olts/${oltId}/vlans/${vlanId}`, body),
  // Line-profiles + service-profiles descobertos direto na OLT (via SSH), com o ID
  // numérico nativo usado por POST .../authorize (line_profile_id/srv_profile_id).
  profilesLive:   (oltId) => api.get(`/v1/olts/${oltId}/profiles-live`),
  onusPending:    (oltId) => api.get(`/v1/olts/${oltId}/onus-pending`),
  // Varredura de autofind sob demanda (uma sessão CLI curta; ignora a cadência do poll).
  // Erros trazem `code` estável: scan_in_progress(409) cli_disabled|cli_breaker_open|cli_busy(503) olt_unreachable|autofind_unreadable(502) ...
  refreshPending: (oltId) => api.post(`/v1/olts/${oltId}/onus-pending/refresh`, {}, { timeout: 100000 }),
  onuPendingDetail: (oltId, pendingId) => api.get(`/v1/olts/${oltId}/onus-pending/${pendingId}`),
  authorizePending: (oltId, pendingId, data) => api.post(`/v1/olts/${oltId}/onus-pending/${pendingId}/authorize`, data || {}),
  dismissPending: (oltId, pendingId) => api.post(`/v1/olts/${oltId}/onus-pending/${pendingId}/dismiss`),
  alarms:         (oltId) => api.get(`/v1/olts/${oltId}/alarms`),
  ackAlarm:       (oltId, alarmId) => api.post(`/v1/olts/${oltId}/alarms/${alarmId}/acknowledge`),
  testConnection: (oltId) => api.post(`/v1/olts/${oltId}/test-connection`),
  // O que o driver da OLT sabe fazer + avisos do estado atual ({ capabilities, warnings[] }) — sem I/O no
  // equipamento. A UI esconde/desabilita o que for false (ver lib/capabilities.js).
  capabilities:   (oltId) => api.get(`/v1/olts/${oltId}/capabilities`),
  triggerPoll:    (oltId) => api.post(`/v1/olts/${oltId}/poll`),
  pollStatus:     (oltId) => api.get(`/v1/olts/${oltId}/poll/status`),
  // Estado da coleta por camadas (T0 inventário / T1 status / T2 métricas) —
  // RevisaoColeta2908 Sessão 2.
  collectionStatus: (oltId) => api.get(`/v1/olts/${oltId}/collection`),
  pollLog:        (oltId, params) => api.get(`/v1/olts/${oltId}/poll-log`, { params }),
  pollJob:        (oltId, jobId) => api.get(`/v1/olts/${oltId}/poll-log/${jobId}`),
  // Histórico de mudanças detectadas na própria OLT (firmware/serial) — doc 04 §4.
  history:        (oltId, params) => api.get(`/v1/olts/${oltId}/history`, { params }),
  // Shell SSH — WebSocket URL builder (upgrade endpoint)
  shellWsUrl:     (oltId) => {
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/v1/olts/${oltId}/shell`;
  },
  // Plain HTTP precheck (auth/role/credentials) called before opening the
  // shell WebSocket — a failed upgrade only ever reaches the browser as a
  // generic onerror/onclose(code=1006), so this surfaces the real reason first.
  shellPrecheck:  (oltId) => api.get(`/v1/olts/${oltId}/shell/precheck`),
};

// v1 · Backup de configuração da OLT (DocsBackupOLT Fase 3/4) — o conteúdo em
// si nunca passa pelo OLT Manager além do download, que é um proxy duplo até
// o SaaS Manager (só ele guarda credencial de S3).
export const oltBackupAPI = {
  list:        (oltId, params) => api.get(`/v1/olts/${oltId}/backups`, { params }),
  trigger:     (oltId) => api.post(`/v1/olts/${oltId}/backups`),
  get:         (oltId, backupId) => api.get(`/v1/olts/${oltId}/backups/${backupId}`),
  download:    (oltId, backupId) => api.get(`/v1/olts/${oltId}/backups/${backupId}/download`, { responseType: 'blob' }),
  restore:     (oltId, backupId) => api.post(`/v1/olts/${oltId}/backups/${backupId}/restore`),
  delete:      (oltId, backupId) => api.delete(`/v1/olts/${oltId}/backups/${backupId}`),
  getSchedule: (oltId) => api.get(`/v1/olts/${oltId}/backup-schedule`),
  setSchedule: (oltId, data) => api.put(`/v1/olts/${oltId}/backup-schedule`, data),
};

// v1 · Perfis de acesso a OLTs (credenciais SSH/SNMP)
export const accessProfileAPI = {
  list:   () => api.get('/v1/access-profiles'),
  get:    (id) => api.get(`/v1/access-profiles/${id}`),
  create: (data) => api.post('/v1/access-profiles', data),
  update: (id, data) => api.put(`/v1/access-profiles/${id}`, data),
  delete: (id) => api.delete(`/v1/access-profiles/${id}`),
};

// v1 · Regras de alarme
export const alarmRuleAPI = {
  list:   () => api.get('/v1/alarm-rules'),
  get:    (id) => api.get(`/v1/alarm-rules/${id}`),
  create: (data) => api.post('/v1/alarm-rules', data),
  update: (id, data) => api.put(`/v1/alarm-rules/${id}`, data),
  delete: (id) => api.delete(`/v1/alarm-rules/${id}`),
  toggle: (id) => api.patch(`/v1/alarm-rules/${id}/toggle`, {}),
};

// v1 · Chaves de API (API Keys)
export const apiKeyAPI = {
  list:   () => api.get('/v1/api-keys'),
  get:    (id) => api.get(`/v1/api-keys/${id}`),
  create: (data) => api.post('/v1/api-keys', data),
  delete: (id) => api.delete(`/v1/api-keys/${id}`),
};

// v1 · Integrações — registro de servidores MCP, catálogo classificado de
// rotas e captura de interesse em ERPs (docs/ApiMcpOLTm/, Sessões 03 e 06).
// integrations:read para leitura; integrations:manage para as mutações de
// servidor/chave.
export const integrationsAPI = {
  listServers:  () => api.get('/v1/integrations/mcp/servers'),
  getServer:    (id) => api.get(`/v1/integrations/mcp/servers/${id}`),
  createServer: (body) => api.post('/v1/integrations/mcp/servers', body),
  updateServer: (id, body) => api.patch(`/v1/integrations/mcp/servers/${id}`, body),
  deleteServer: (id) => api.delete(`/v1/integrations/mcp/servers/${id}`),
  mintKey:      (id, body) => api.post(`/v1/integrations/mcp/servers/${id}/keys`, body || {}),
  revokeKey:    (id, kid) => api.delete(`/v1/integrations/mcp/servers/${id}/keys/${kid}`),
  catalog:      () => api.get('/v1/integrations/catalog'),
  erpInterest:  (body) => api.post('/v1/integrations/erp/interest', body),
  // `window` é '24h' | '7d' | '30d' (o backend só suporta essas três janelas).
  insights:     (window) => api.get('/v1/integrations/mcp/insights', { params: window ? { window } : {} }),
};

// v1 · Clientes (Customers) + assign ONU
export const customerAPI = {
  list:   (params) => api.get('/v1/customers', { params }),
  get:    (id) => api.get(`/v1/customers/${id}`),
  create: (data) => api.post('/v1/customers', data),
  update: (id, data) => api.put(`/v1/customers/${id}`, data),
  delete: (id) => api.delete(`/v1/customers/${id}`),
  assignOnu: (id, data) => api.post(`/v1/customers/${id}/assign-onu`, data),
  listOnus: (id) => api.get(`/v1/customers/${id}/onus`),
};

// v1 · Billing (licenciamento por OLT)
export const billingAPI = {
  list:       () => api.get('/v1/billing/olts'),
  gateways:   () => api.get('/v1/billing/gateways'),
  checkout:   (oltId, gatewayType) => api.post(`/v1/billing/olts/${oltId}/checkout`, { gateway_type: gatewayType || '' }),
  sync:       () => api.post('/v1/billing/sync'),
  portalLink: () => api.post('/v1/billing/portal-link'),
  payments:   () => api.get('/v1/billing/payments'),
};

// v1 · Fabricantes de equipamentos
export const manufacturerAPI = {
  list:   () => api.get('/v1/equipment/manufacturers'),
  create: (data) => api.post('/v1/equipment/manufacturers', data),
};

// v1 · Canais de notificação (Telegram, Email)
export const notificationChannelAPI = {
  list:   () => api.get('/v1/notification-channels'),
  get:    (id) => api.get(`/v1/notification-channels/${id}`),
  create: (data) => api.post('/v1/notification-channels', data),
  update: (id, data) => api.put(`/v1/notification-channels/${id}`, data),
  delete: (id) => api.delete(`/v1/notification-channels/${id}`),
  test:   (id) => api.post(`/v1/notification-channels/${id}/test`),
};

// Catálogo LEGADO de tipos de ONU (`/onu-types`, sem v1) — é o único vinculado de verdade
// a ONUs reais (onus.onu_type_id referencia esta tabela). NÃO confundir com o catálogo solto
// `/v1/onu-types` (equipment_models) — esse não tem relação com nenhuma ONU provisionada
// (doc 05 §2). Auto-populado a partir do autofind da OLT; PUT serve pra enriquecer um
// registro auto-criado (foto, descrição, capacidades etc).
export const onuTypeAPI = {
  list:   () => api.get('/onu-types'),
  get:    (id) => api.get(`/onu-types/${id}`),
  create: (data) => api.post('/onu-types', data),
  update: (id, data) => api.put(`/onu-types/${id}`, data),
  delete: (id) => api.delete(`/onu-types/${id}`),
};

// Catálogo local de tipos de OLT (`/olt-types`, migration 036) — metadado
// adicional, ainda não usado pelo formulário de criação/edição de OLT
// (que continua com vendor/hw livres). Mirror de onuTypeAPI.
export const oltTypeAPI = {
  list:   () => api.get('/olt-types'),
  get:    (id) => api.get(`/olt-types/${id}`),
  create: (data) => api.post('/olt-types', data),
  update: (id, data) => api.put(`/olt-types/${id}`, data),
  delete: (id) => api.delete(`/olt-types/${id}`),
};

// Catálogo central de equipamentos no SaaS Manager (Sessão 14) — browse +
// importação pra dentro de onuTypeAPI/oltTypeAPI locais. Requer a instância
// estar conectada a um SaaS Manager (503 se não estiver).
export const catalogAPI = {
  listOltTypes: () => api.get('/catalog/olt-types'),
  listOnuTypes: () => api.get('/catalog/onu-types'),
  importOltType: (catalogId) => api.post(`/olt-types/import/${catalogId}`),
  importOnuType: (catalogId) => api.post(`/onu-types/import/${catalogId}`),
};

// v1 · Perfis de serviço GPON (T-CONT, DBA, VLAN, QoS)
export const serviceProfileAPI = {
  list:   () => api.get('/v1/service-profiles'),
  get:    (id) => api.get(`/v1/service-profiles/${id}`),
  create: (data) => api.post('/v1/service-profiles', data),
  update: (id, data) => api.put(`/v1/service-profiles/${id}`, data),
  delete: (id) => api.delete(`/v1/service-profiles/${id}`),
};

// v1 · Configurações de notificação (e-mail, SMTP)
export const notificationSettingsAPI = {
  get:    () => api.get('/v1/settings/notifications'),
  update: (data) => api.put('/v1/settings/notifications', data),
};

// Healthcheck
export const healthAPI = {
  check:  () => axios.get(`${BACKEND_URL}/health`),
};

export const onuAPI = {
  // list(params) — paginação server-side ([5.03]): page/per_page (ou limit/offset),
  // search, sort (`-rx_power` = desc / `sort_dir`), status/olt_id/zone_id/odb_id.
  // O corpo é um array puro; o total do filtro vem no header `x-total-count` e
  // `x-has-more` diz se há próxima página.
  list: (params) => api.get('/onus', { params }),
  // Exporta a lista (CSV) montada e streamada pelo servidor — usado pelo botão
  // "Exportar" das telas Configuradas/Diagnóstico em vez de serializar um array
  // de 30-50k ONUs em memória. Aceita status/olt_id/zone_id/odb_id/search.
  exportCsv: (params) => api.get('/onus/export', { params, responseType: 'blob', timeout: 120000 }),
  unconfigured: () => api.get('/onus/unconfigured'),
  get: (id) => api.get(`/onus/${id}`),
  create: (data) => api.post('/onus', data),
  // name/customer/address/contact/zone_id/odb_id/cto_port são aceitos (doc 02 §3) — envie só
  // o que mudou. zone_id/odb_id: uuid pra vincular, '' pra desvincular, omitir pra não mexer.
  // Se `name` mudar, o backend envia isso pro equipamento real de forma síncrona
  // (502 "OLT rejected rename" se a OLT recusar — nesse caso nada é salvo).
  update: (id, data) => api.put(`/onus/${id}`, data),
  delete: (id) => api.delete(`/onus/${id}`),
  metrics: (id) => api.get(`/onus/${id}/metrics`),
  // Ciclo de vida real ("ont deactivate"/"ont activate" no equipamento) — reversível.
  // Doc 04 §3: a resposta agora é o objeto ONU completo (rx_power/tx_power/etc. já
  // atualizados via leitura síncrona pós-ação), não mais só { id, status }.
  authorize: (id) => api.post(`/onus/${id}/authorize`),
  deauthorize: (id) => api.post(`/onus/${id}/deauthorize`),
  reboot: (id) => api.post(`/onus/${id}/reboot`),
  // Ressincronizar (PlanoRevisao2309 fase 5). mode 'preview' (padrão) só compara o que o
  // sistema tem como desejado com a OLT e devolve o diff; 'apply' reaplica só o que falta e
  // lê de volta. includeWan = também reaplica o modo WAN (pode derrubar a conexão).
  // Leva mais de uma sessão CLI → timeout largo.
  resync: (id, mode = 'preview', includeWan = false) =>
    api.post(`/onus/${id}/resync`, { mode, include_wan: includeWan }, { timeout: 95000 }),
  // Substituir ONU (SN): troca o serial mantendo ONT-ID, perfis e service-ports na OLT
  // ("ont modify ... sn") e relê a porta para confirmar. A linha da ONU mantém o id; o
  // serial antigo vira histórico (reinstalled_from_id). Erros vêm com `code` estável.
  replaceSerial: (id, serial) => api.post(`/onus/${id}/replace-serial`, { serial }, { timeout: 95000 }),
  // Restaurar padrões (A3): "ont factory-setting-restore" — devolve o CPE à configuração de
  // fábrica (o cliente perde a internet; NÃO é reinício). Exige confirmSerial = serial da ONU.
  restoreDefaults: (id, confirmSerial) => api.post(`/onus/${id}/restore-defaults`, { confirm_serial: confirmSerial }, { timeout: 95000 }),
  // Mover ONU (A4): body { mode: 'preview'|'apply', dest_slot, dest_port, confirm_serial?, acknowledge_online? }.
  // preview só lê (origem + destino); apply remove da origem, recria no destino e aponta a MESMA linha
  // para a nova porta. Falha no meio é retomável repetindo o mesmo destino. Só na mesma OLT.
  move: (id, body) => api.post(`/onus/${id}/move`, body, { timeout: 200000 }),
  // As 3 rotas "ao vivo" (doc 04 §1) fazem leitura real na OLT a cada chamada —
  // nunca chamar em polling automático, só sob demanda (clique de botão).
  // status-live é SNMP-first (RevisaoColeta2908 Sessão 7): abrir a ONU e ver
  // sinal/status NÃO abre sessão CLI; ?include=service_ports força o passo CLI.
  statusLive: (id) => api.get(`/onus/${id}/status-live`, { timeout: 25000 }),
  configLive: (id) => api.get(`/onus/${id}/config-live`, { timeout: 25000 }),
  softwareLive: (id) => api.get(`/onus/${id}/software-live`, { timeout: 25000 }),
  // Modo ONU/WAN real ("display ont wan-info", item 19 sessão 2) — mesmo custo/convenção
  // das outras 3 leituras ao vivo acima.
  wanLive: (id) => api.get(`/onus/${id}/wan-live`, { timeout: 25000 }),
  // IP de gerenciamento + TR-069 (somente leitura, PlanoRevisao2309 fase 6): TR-069 vem do perfil de
  // linha (banco), o IP é um "display ont ipconfig" ao vivo — só sob demanda.
  mgmtLive: (id) => api.get(`/onus/${id}/mgmt-live`, { timeout: 25000 }),
  // Leitura ao vivo (display sippstnuser attribute) do estado de voz SIP — só sob demanda, nunca em polling.
  voipLive: (id) => api.get(`/onus/${id}/voip-live`, { timeout: 30000 }),
  // Escrita real do modo de IP WAN (DHCP/Estático/PPPoE, "ont ipconfig" — item 19 sessão 3).
  // Só o modo de obtenção de IP; Bridge/Roteamento continua fora (sessão 4). Backend revalida
  // supports_wan_config antes de mandar qualquer comando pra OLT.
  updateWanIpConfig: (id, data) => api.put(`/onus/${id}/wan-config`, data, { timeout: 25000 }),
  // Escrita real de Bridge/Roteamento ("ont wan-profile"/"ont wan-config" — item 19 sessão 4).
  // Endpoint separado do de cima (ver UpdateONUWANModeRequest no backend): fluxo de 2 comandos,
  // maior risco (pode isolar o cliente da rede), confirmação obrigatória via leitura de volta.
  // vlanId (opcional, só usado em mode='bridge' — Docsvalidacao1608 item 5): backend também
  // aplica a VLAN nativa + service-port reais na porta Ethernet do ONT, não só o wan-profile.
  updateWanMode: (id, mode, vlanId) => api.put(`/onus/${id}/wan-mode`, { mode, vlan_id: vlanId ?? null }, { timeout: 30000 }),
  // Histórico de mudanças (status_change / external_change / removed) — doc 04 §4.
  history: (id, params) => api.get(`/onus/${id}/history`, { params }),
  // Configuração de porta Ethernet (LAN/UNI) da ONU — doc 05 §3. Lista vazia é normal
  // (tabela não populada pelo poll periódico, só por esta API).
  ethernetPorts: (id) => api.get(`/onus/${id}/ethernet-ports`),
  updateEthernetPort: (id, portIndex, data) => api.put(`/onus/${id}/ethernet-ports/${portIndex}`, data),
  // VLAN nativa da porta ("ont port native-vlan") — mecanismo SEPARADO do modo/vlan_id
  // acima ("ont port vlan"): comandos Huawei diferentes, não uma alternativa. Sempre lê
  // o valor atual antes de escrever (a OLT rejeita reenviar o mesmo valor) e confirma
  // lendo de volta — a resposta é o valor real após a leitura de confirmação.
  updateEthernetPortNativeVlan: (id, portIndex, vlanId) =>
    api.put(`/onus/${id}/ethernet-ports/${portIndex}/native-vlan`, { vlan_id: vlanId }),
  // Service-ports reais da ONU ("Perfis de velocidade" — item 17). GET é leitura ao vivo
  // na OLT (mesmo custo de status-live). PUT cria/substitui o único service-port que esta
  // API gerencia (só VLAN — banda não é configurável, ver backend docs.go). DELETE remove
  // esse mesmo service-port; ao contrário da exclusão da ONU inteira, uma rejeição da OLT
  // aqui vira erro (502), não é engolida silenciosamente.
  servicePorts: (id) => api.get(`/onus/${id}/service-ports`, { timeout: 25000 }),
  upsertServicePort: (id, data) => api.put(`/onus/${id}/service-ports`, data),
  deleteServicePort: (id) => api.delete(`/onus/${id}/service-ports`),
  // Modelo por intenção (FiberHome: whitelist + serviço): serviços e banda lidos da OLT por último, e
  // troca de VLAN/banda. Huawei responde supported:false (GET) / 422 use_service_ports (PUT).
  services: (id) => api.get(`/onus/${id}/services`),
  setServices: (id, data) => api.put(`/onus/${id}/services`, data, { timeout: 60000 }),
  // Consumo de banda — doc 05 §4. `bandwidth` é histórico de poll (~1 ponto/5min);
  // `bandwidthLive` é leitura sob demanda pra gráfico "ao vivo" (available:false é um
  // estado permanente esperado no driver Huawei atual, não um erro transitório).
  bandwidth: (id, params) => api.get(`/onus/${id}/bandwidth`, { params }),
  bandwidthLive: (id) => api.get(`/onus/${id}/bandwidth-live`),
  // Grade paginada de tráfego diário por ONU (tela "Gráficos > Traffic" no padrão
  // SmartOLT) — cada item traz a série de onu_metrics das últimas 24h já pronta pro
  // mini-gráfico. Filtros: olt_id/zone_id/odb_id/board/port, todos opcionais.
  trafficGrid: (params) => api.get('/onus/traffic-grid', { params }),
};

// v1 · Ações em lote — job assíncrono aplicado a várias ONUs de uma vez
// (DocsSmartOltInspiracao 02). create() responde 202 com { job_id, total_count }
// assim que o job é criado; o progresso real (pending→running→success/failed
// por ONU) é acompanhado via get(job_id), feito via polling pelo caller.
export const bulkJobAPI = {
  create: (data) => api.post('/v1/onus/bulk-actions', data),
  // list(params) — { from, to, status, action, user_id, q, limit, offset } todos
  // opcionais. Resposta { data, total } + cabeçalho X-Has-More (a rota antiga
  // devolvia um array puro — callers tratam as duas formas).
  list: (params) => api.get('/v1/bulk-jobs', { params }),
  // get(id, params) — params { item_limit, item_offset } paginam os itens do job.
  get: (id, params) => api.get(`/v1/bulk-jobs/${id}`, { params }),
};

// Ações automáticas (PlanoRevisao2309 / Fase 8, docs/PlanoRevisao2309/
// 04-acoes-automaticas.md): tarefas com prazo que autorizam / ressincronizam /
// movem ONUs sozinhas a partir do autofind de UMA OLT. Toda falha traz `code`
// estável (auto_actions_disabled, auto_task_active, preset_incomplete,
// real_execution_unavailable, task_not_active, ...) — a UI ramifica por status
// + code, nunca pelo texto. Tempo real = polling (3 s ativa / 15 s parada).
export const autoTaskAPI = {
  // create({ olt_ids[], actions[], preset_id?, duration_minutes, dry_run?, max_items?,
  //          slots?, ports?, scan_interval_s?, placeholder_serials? }) → 201 { data: [task] }
  create: (data) => api.post('/v1/auto-tasks', data),
  // list({ olt_id, user (uuid|api|system), action, status, from, to, limit, offset })
  // → { data, total, auto_actions_enabled } + X-Has-More.
  list: (params) => api.get('/v1/auto-tasks', { params }),
  // get(id) → tarefa + progress { total, processed, success, failed, simulated, skipped, pending, running, by_action }
  get: (id) => api.get(`/v1/auto-tasks/${id}`),
  // items(id, { action, status, serial, since, limit, offset }) → { data, total, server_time };
  // since = server_time da resposta anterior devolve só o que mudou.
  items: (id, params) => api.get(`/v1/auto-tasks/${id}/items`, { params }),
  stop: (id, reason) => api.post(`/v1/auto-tasks/${id}/stop`, reason ? { reason } : {}),
  // "Parar ações automáticas" — todas do tenant, ou só as de uma OLT (olt_id).
  stopAll: (oltId) => api.post('/v1/auto-tasks/stop', oltId ? { olt_id: oltId } : {}),
  // restart(id, durationMinutes?) — pausada por erro com janela aberta = retomada
  // (200 resumed:true); as demais = clone com nova janela (201).
  restart: (id, durationMinutes) => api.post(`/v1/auto-tasks/${id}/restart`, durationMinutes ? { duration_minutes: durationMinutes } : {}),
  retryItem: (id, itemId) => api.post(`/v1/auto-tasks/${id}/items/${itemId}/retry`, {}),
  // Tarefa ativa da OLT (ou { data: null }) — cartão "ativa até HH:MM".
  activeForOlt: (oltId) => api.get(`/v1/olts/${oltId}/auto-task`),
};

// Auditoria de comandos das OLTs (docs/revisao2509/06-relatorio-auditoria-de-comandos-olt.md,
// Fase 12) — GET /v1/olt-cli-commands/*, gate único `olt_cli_audit:read`.
// Filtros de list/summary/export: from, to (RFC 3339 com Z; padrão últimas 24 h, janela
// máx. 31 d ⇒ 400), olt_id[], status[], actor_type[], user_id, session_id, origin[] (valor
// terminado em ':' = prefixo, ex. 'poll:'), is_write, has_error, kind[] (padrão sem
// bootstrap; 'all' = tudo), q (trecho do comando), q_output, min_duration_ms, since_id
// (modo ao vivo: só ids maiores). list ainda aceita limit (≤ 200) e cursor (next_cursor
// da página anterior). Arrays vão como parâmetro repetido (olt_id=a&olt_id=b).
function cliQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    (Array.isArray(v) ? v : [v]).forEach((item) => {
      if (item !== undefined && item !== null && item !== '') qs.append(k, String(item));
    });
  });
  return qs.toString();
}
const cliSerializer = { serialize: cliQuery };

export const cliAuditAPI = {
  // → { commands: [...], next_cursor, limit, from, to }; cada item traz output_preview
  // (200 caracteres), output_bytes e output_truncated — a resposta inteira só em get().
  list: (params) => api.get('/v1/olt-cli-commands', { params, paramsSerializer: cliSerializer }),
  // → { command (com `output`), previous_id, next_id, session_size }
  get: (id) => api.get(`/v1/olt-cli-commands/${id}`),
  // → { session_id, current_id, commands: [...], truncated }
  session: (id) => api.get(`/v1/olt-cli-commands/${id}/session`),
  // → { total, errors, error_rate, writes, sessions, p50_ms, p95_ms, bucket_seconds, statuses,
  //     actor_types, timeline[{ts,total,errors,writes}], top_origins, top_users, top_olts }
  summary: (params) => api.get('/v1/olt-cli-commands/summary', { params, paramsSerializer: cliSerializer }),
  // → { olts, origins, users, statuses, actor_types, kinds } da janela from/to (demais filtros ignorados)
  facets: (params) => api.get('/v1/olt-cli-commands/facets', { params, paramsSerializer: cliSerializer }),
  // URL do CSV (`include_output=1` adiciona a resposta do equipamento). O endpoint exige o
  // Authorization do axios — um <a href> não autentica; para baixar use export().
  exportUrl: (params) => `${API}/v1/olt-cli-commands/export?${cliQuery(params)}`,
  // CSV como blob (streaming no servidor, até 100 000 linhas; a última linha diz se cortou).
  export: (params) => api.get('/v1/olt-cli-commands/export', {
    params, paramsSerializer: cliSerializer, responseType: 'blob', timeout: 300000,
  }),
};

// Feed agregado de mudanças externas (DocsSmartOltInspiracao/
// 05-tela-agregada-divergencia-externa.md) — todas as ONUs/OLTs do tenant.
export const externalChangeAPI = {
  list: (params) => api.get('/v1/history/external-changes', { params }),
};

// Zonas — o backend só expõe list/create/delete (ver rbac_matrix.go). Não há
// GET/PUT /zones/{id}: a edição de zona não existe na API, e get(id) por ID
// nunca foi usado (a UI sempre carrega a lista inteira via list()).
export const zoneAPI = {
  list: () => api.get('/zones'),
  create: (data) => api.post('/zones', data),
  delete: (id) => api.delete(`/zones/${id}`),
};

// CTO / splitter óptico ("ODB" no banco) — CRUD completo, doc 05 §1.
export const odbAPI = {
  list: () => api.get('/odbs'),
  get: (id) => api.get(`/odbs/${id}`),
  create: (data) => api.post('/odbs', data),
  update: (id, data) => api.put(`/odbs/${id}`, data),
  delete: (id) => api.delete(`/odbs/${id}`),
};

export const speedProfileAPI = {
  list: () => api.get('/speed-profiles'),
  create: (data) => api.post('/speed-profiles', data),
  update: (id, data) => api.put(`/speed-profiles/${id}`, data),
  delete: (id) => api.delete(`/speed-profiles/${id}`),
};

export const authPresetAPI = {
  list: () => api.get('/auth-presets'),
  create: (data) => api.post('/auth-presets', data),
  get: (id) => api.get(`/auth-presets/${id}`),
  update: (id, data) => api.put(`/auth-presets/${id}`, data),
  delete: (id) => api.delete(`/auth-presets/${id}`),
};

export const auditLogAPI = {
  list: () => api.get('/audit-logs'),
};

export const taskAPI = {
  list: () => api.get('/tasks'),
  create: (data) => api.post('/tasks', data),
};

export const alertAPI = {
  // params: { q, olt_id, severity, status, date_from, date_to, order } — todos opcionais.
  list: (params) => api.get('/alerts', { params }),
  acknowledge: (id) => api.put(`/alerts/${id}/acknowledge`),
};

export const leadAPI = {
  create: (data) => api.post('/leads', data),
};

// Central de Relatórios (docs/DocsRelatorios/). Parte 01 scaffold — the
// per-report methods are added by the follow-up parts under `/v1/reports`.
export const reportsAPI = {
  // Parte 02 — mapa geográfico da rede (docs/DocsRelatorios/02-mapa-geografico.md)
  geo: () => api.get('/v1/reports/geo'),
  setCoords: (kind, id, body) => api.put(`/v1/reports/geo/${kind}/${id}`, body),
  // Parte 03 — ocupação de CTOs no mapa (docs/DocsRelatorios/03-ocupacao-ctos-mapa.md)
  ctoOccupancy: () => api.get('/v1/reports/cto-occupancy'),
  // Parte 04 — topologia da OLT (docs/DocsRelatorios/04-topologia-olt.md).
  // oltTopology: árvore agregada (placas/portas/CTOs) montada só do banco.
  // oltTopologyOnus: ONUs de uma porta (params: { port_id, odb_id, limit, offset }).
  // odb_id='none' devolve as ONUs sem CTO; sem port_id lista a OLT inteira (busca global).
  oltTopology: (oltId) => api.get(`/v1/reports/olts/${oltId}/topology`),
  oltTopologyOnus: (oltId, params) => api.get(`/v1/reports/olts/${oltId}/topology/onus`, { params }),
  // Parte 05 — diagrama + ocupação de portas PON (docs/DocsRelatorios/05-portas-pon.md).
  // Uma resposta (linha por porta PON + `totals` da frota) serve os dois
  // relatórios: /app/reports/pon-capacity (tabela) e /app/reports/pon-diagram (matriz).
  // params: { olt_id?, zone_id? }. Drawer de ONUs por porta: onuAPI.list({ pon_port_id }).
  ponPorts: (params) => api.get('/v1/reports/pon-ports', { params }),
  // Parte 06 — clientes por plano de velocidade (docs/DocsRelatorios/06-onus-tipo-e-planos.md).
  // Agrega onu_speed_profiles + speed_profiles + onus pelo par download/upload.
  // params: { olt_id?, zone_id? }. O relatório de ONUs por tipo/fabricante é 100%
  // frontend (usa onuAPI.list paginado até o fim).
  speedPlans: (params) => api.get('/v1/reports/speed-plans', { params }),
  // Parte 07 — histórico de trocas de ONU (docs/DocsRelatorios/07-trocas-de-onu.md).
  // params: { from?, to? (RFC3339), customer?, olt_id?, reason?, type? ('swap'|'reinstall') }.
  // setSwapReason: reclassifica o motivo (rma|upgrade|defect|reinstall|unknown).
  onuSwaps: (params) => api.get('/v1/reports/onu-swaps', { params }),
  setSwapReason: (id, reason) => api.patch(`/v1/reports/onu-swaps/${id}`, { reason }),
  // Parte 09 — disponibilidade / uptime (docs/DocsRelatorios/09-uptime-sla.md).
  // params: { from?, to? (RFC3339) }. Default backend: últimos 30 dias.
  // Resposta: { data: { from, to, since, olts:[...], totals:{...} } } — `since`
  // é a 1ª transição de status já registrada (null se o coletor ainda não
  // gravou nenhuma). Fonte: tabela olt_status_history (sem backfill).
  oltUptime: (params) => api.get('/v1/reports/olt-uptime', { params }),
  // Parte 10 — consumo por cliente / fair use (docs/DocsRelatorios/10-consumo-cliente.md).
  // Agrega onu_metrics por cliente (soma o tráfego das ONUs do cliente por bucket
  // antes do percentil). params: { period? ('24h'|'7d'|'30d'), olt_id?, zone_id? }.
  // Resposta: { data: { from, to, bucket_seconds, customers:[...], truncated, totals } }.
  // customerConsumptionSeries: série agregada + resumo + ONUs do cliente p/ o drawer.
  customerConsumption: (params) => api.get('/v1/reports/customer-consumption', { params }),
  customerConsumptionSeries: (customer, params) =>
    api.get(`/v1/reports/customer-consumption/${encodeURIComponent(customer)}/series`, { params }),
  // Parte 11 — usuários e permissões / RBAC (docs/DocsRelatorios/11-rbac.md).
  // Resposta: { data: { catalog:[...], groups:[...], users:[...], totals:{...} } }
  // com `effective` já resolvido por usuário (["*"] p/ owner/admin/curinga).
  rbac: () => api.get('/v1/reports/rbac'),
  // Parte 13 — conformidade & backups (docs/DocsRelatorios/13-conformidade-backups.md).
  // Visão agregada de olt_backups + schedule por OLT, com grau de conformidade
  // ok/warning/critical + totals. A rota só existe quando BACKUP_FEATURE_ENABLED
  // está ligado no backend — um 404 significa "recurso de backup desativado".
  // O histórico por OLT e os botões de baixar/disparar reusam oltBackupAPI.
  backupsCompliance: () => api.get('/v1/reports/backups'),
};

export default api;
